import type { JSX } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import type {
  CrashCause,
  PaceReport,
  PlaytestCrash,
  PlaytestRace,
  PlaytestSketch,
} from '../../sim/bot/index.ts';
import type { Vec2 } from '../../sim/types.ts';

const CAUSE_LABEL: Readonly<Record<CrashCause, string>> = {
  aim: 'Aim error',
  cornered: 'Came in too fast',
  other: 'Unexplained',
};

/** One stroke colour per seed's track; crash markers take the cause colour. */
const TRACK_CLASS = ['is-seed-a', 'is-seed-b', 'is-seed-c'] as const;

function formatClock(ms: number): string {
  const seconds = ms / 1000;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${(seconds - minutes * 60).toFixed(1).padStart(4, '0')}`;
}

function crashKey(seed: number, crash: PlaytestCrash): string {
  return `${seed}-${crash.turn}`;
}

function points(list: readonly Vec2[]): string {
  return list.map((point) => `${Math.round(point.x)},${Math.round(point.y)}`).join(' ');
}

/** Bounding box of the tracks and gates, padded, so a 16 km map does not shrink the race to a dot. */
function viewBox(sketch: PlaytestSketch, races: readonly PlaytestRace[]): string {
  const all: Vec2[] = [
    ...races.flatMap((race) => race.path),
    ...sketch.gates.flatMap((gate) => [gate.a, gate.b]),
    ...sketch.start,
  ];

  if (all.length === 0) {
    return '0 0 1 1';
  }

  const xs = all.map((point) => point.x);
  const ys = all.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const width = Math.max(...xs) - minX;
  const height = Math.max(...ys) - minY;
  const pad = Math.max(width, height) * 0.08 + 200;

  return `${Math.round(minX - pad)} ${Math.round(minY - pad)} ${Math.round(width + 2 * pad)} ${Math.round(height + 2 * pad)}`;
}

function Sketch({
  sketch,
  races,
  focus,
  onFocus,
}: {
  sketch: PlaytestSketch;
  races: readonly PlaytestRace[];
  focus: string | null;
  onFocus: (key: string | null) => void;
}): JSX.Element {
  const box = viewBox(sketch, races);
  const scale = Number(box.split(' ')[2]) / 400;

  return (
    <svg
      class="bot-playtest-sketch"
      viewBox={box}
      role="img"
      aria-label="Playtest tracks and crashes"
    >
      {sketch.start.length > 0 ? <polygon class="sketch-start" points={points(sketch.start)} /> : null}
      {sketch.land.map((outline, index) => (
        <polygon key={index} class="sketch-land" points={points(outline)} />
      ))}
      {sketch.gates.map((gate, index) => (
        <line
          key={index}
          class={gate.goal ? 'sketch-gate is-goal' : 'sketch-gate'}
          x1={gate.a.x}
          y1={gate.a.y}
          x2={gate.b.x}
          y2={gate.b.y}
        />
      ))}
      {races.map((race, index) => (
        <polyline
          key={race.seed}
          class={`sketch-track ${TRACK_CLASS[index % TRACK_CLASS.length]}`}
          points={points(race.path)}
        />
      ))}
      {races.flatMap((race) =>
        race.crashes.map((crash) => {
          const key = crashKey(race.seed, crash);
          return (
            <circle
              key={key}
              class={`sketch-crash is-${crash.cause}${focus === key ? ' is-focus' : ''}`}
              cx={crash.position.x}
              cy={crash.position.y}
              r={(focus === key ? 9 : 6) * scale}
              data-crash={key}
              onPointerEnter={() => onFocus(key)}
              onPointerLeave={() => onFocus(null)}
            >
              <title>
                Seed {race.seed}, turn {crash.turn}: {CAUSE_LABEL[crash.cause]}
              </title>
            </circle>
          );
        }),
      )}
    </svg>
  );
}

function Legend({ races }: { races: readonly PlaytestRace[] }): JSX.Element {
  return (
    <ul class="bot-sketch-legend">
      {races.map((race, index) => (
        <li key={race.seed}>
          <span class={`bot-sketch-swatch is-track ${TRACK_CLASS[index % TRACK_CLASS.length]}`} />
          Seed {race.seed}
        </li>
      ))}
      {(['aim', 'cornered'] as const).map((cause) => (
        <li key={cause}>
          <span class={`bot-sketch-swatch is-dot sketch-crash is-${cause}`} />
          {CAUSE_LABEL[cause]}
        </li>
      ))}
    </ul>
  );
}

/**
 * The sketch at full size, portaled to `#ui-root` (the trait panel is transformed, which would
 * pin a fixed backdrop to it). `editor-log-modal` is the class the Escape router defers to, so
 * Escape closes this and not the workshop.
 */
export function SketchModal({
  sketch,
  races,
  focus,
  onFocus,
  onClose,
  title,
}: {
  sketch: PlaytestSketch;
  races: readonly PlaytestRace[];
  focus: string | null;
  onFocus: (key: string | null) => void;
  onClose: () => void;
  /** Heads the modal, e.g. which test it is. */
  title?: string;
}): JSX.Element {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onClose();
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <div class="bot-sketch-modal-backdrop ui-chrome" onClick={onClose}>
      <div
        class="editor-log-modal bot-sketch-modal ui-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Playtest course"
        onClick={(event) => event.stopPropagation()}
      >
        <div class="bot-sketch-modal-header">
          {title ? <h2 class="bot-sketch-modal-title">{title}</h2> : null}
          <Legend races={races} />
          <button type="button" class="ui-button" onClick={onClose}>
            Close
          </button>
        </div>
        <Sketch sketch={sketch} races={races} focus={focus} onFocus={onFocus} />
      </div>
    </div>,
    document.getElementById('ui-root') ?? document.body,
  );
}

function CrashCard({
  seed,
  crash,
  focus,
  onFocus,
}: {
  seed: number;
  crash: PlaytestCrash;
  focus: string | null;
  onFocus: (key: string | null) => void;
}): JSX.Element {
  const key = crashKey(seed, crash);

  return (
    <li
      class={`bot-playtest-crash is-${crash.cause}${focus === key ? ' is-focus' : ''}`}
      data-crash-cause={crash.cause}
      onPointerEnter={() => onFocus(key)}
      onPointerLeave={() => onFocus(null)}
    >
      <p class="bot-playtest-crash-head">
        <strong>Turn {crash.turn}</strong> · leg {crash.leg} · {CAUSE_LABEL[crash.cause]}
        <span class="ui-hint">
          {' '}
          · {crash.speedKmh} km/h, hit at {crash.impactKmh} km/h, {crash.crossingDeg}°
        </span>
      </p>
      <p class="bot-playtest-crash-why">{crash.summary}</p>
      {crash.factors.length > 0 ? (
        <ul class="bot-playtest-factors">
          {crash.factors.map((factor) => (
            <li key={factor}>{factor}</li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function RaceLog({
  race,
  focus,
  onFocus,
}: {
  race: PlaytestRace;
  focus: string | null;
  onFocus: (key: string | null) => void;
}): JSX.Element {
  const crashes = race.crashes.length;

  return (
    <details class="bot-playtest-race" data-seed={race.seed} open={crashes > 0}>
      <summary>
        Seed {race.seed} · {race.finished ? formatClock(race.timeMs) : 'did not finish'} · {race.turns}{' '}
        turns · {crashes} {crashes === 1 ? 'crash' : 'crashes'}
      </summary>
      {crashes > 0 ? (
        <ol class="bot-playtest-crashes">
          {race.crashes.map((crash) => (
            <CrashCard key={crash.turn} seed={race.seed} crash={crash} focus={focus} onFocus={onFocus} />
          ))}
        </ol>
      ) : (
        <p class="ui-hint">A clean race.</p>
      )}
      <details class="bot-playtest-turns">
        <summary>Turn by turn</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Turn</th>
              <th scope="col">Leg</th>
              <th scope="col">km/h</th>
              <th scope="col" title="Turns lost by slipping to a worse clean click">Slip</th>
              <th scope="col" title="How far aim error moved the click">Aim (m)</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            {race.log.map((turn) => (
              <tr key={turn.turn} class={turn.crashed ? 'is-crash' : undefined}>
                <td>{turn.turn}</td>
                <td>{turn.leg}</td>
                <td>{turn.speedKmh}</td>
                <td>{turn.slipTurns > 0 ? turn.slipTurns : ''}</td>
                <td>{turn.aimOffsetM > 0 ? turn.aimOffsetM : ''}</td>
                <td>
                  {[turn.fallback ? 'no clean click' : '', turn.crashed ? 'crash' : '']
                    .filter(Boolean)
                    .join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </details>
  );
}

/**
 * The quick playtest's log: a sketch of the course with each race's track and crash markers,
 * then one expandable entry per race with its crashes explained and a turn-by-turn table.
 * Hovering a crash card or marker highlights the other.
 */
export function PlaytestLog({
  report,
  sketch,
}: {
  report: PaceReport;
  sketch: PlaytestSketch;
}): JSX.Element {
  const [focus, setFocus] = useState<string | null>(null);
  const [full, setFull] = useState(false);
  const crashes = report.races.reduce((sum, race) => sum + race.crashes.length, 0);

  return (
    <details class="bot-playtest-log" data-bot-playtest-log>
      <summary>
        Playtest log · Expert {formatClock(report.expertMs)} · {crashes} {crashes === 1 ? 'crash' : 'crashes'}
      </summary>
      <div class="bot-playtest-log-body">
        <button
          type="button"
          class="bot-playtest-sketch-open"
          aria-label="Show the course full size"
          title="Show full size"
          onClick={() => setFull(true)}
        >
          <Sketch sketch={sketch} races={report.races} focus={focus} onFocus={setFocus} />
        </button>
        <div class="bot-playtest-races">
          {report.sameEverySeed ? (
            <p class="ui-hint" data-bot-playtest-same>
              This bot has no slips and no aim error, so the seed changes nothing: one race stands
              for every seed.
            </p>
          ) : null}
          {report.races.map((race) => (
            <RaceLog key={race.seed} race={race} focus={focus} onFocus={setFocus} />
          ))}
        </div>
      </div>
      {full ? (
        <SketchModal
          sketch={sketch}
          races={report.races}
          focus={focus}
          onFocus={setFocus}
          onClose={() => setFull(false)}
        />
      ) : null}
    </details>
  );
}
